import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Table from "components/Table/CustomTable";
import CustomInput from "components/CustomInput/CustomOutlinedInput";
import Button from "components/CustomButtons/CustomButton";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CustomModal from "components/CustomModal/Modal";
import { getUsersList, changePassword } from "views/Services/UsersService";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { sanatizeMessageHtml } from "components/Utils/utils";
import { VpnKey } from "@material-ui/icons";
import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";
import Snackbar from "components/Snackbar/Snackbar";

const useStyles = makeStyles(styles);

export default function UsersList() {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [modalChangePass, setModalChangePass] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [messageToast, setMessageToast] = useState("");
  const [typeToast, setTypeToast] = useState("danger");
  const [toastMessage, setToastMessage] = useState(false);
  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const [message, setMessage] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const rowsForPage = 10;
  const tableRef = useRef();

  useEffect(() => {
    if (localStorage.getItem("userType") !== "1") {
      window.location.href = "/admin/home";
    }
  }, []);

  useEffect(() => {
    getUsers(currentOffset);
  }, [dispatch, filter]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setUsers([]);
      handleChangePage(0);
      goToFirstPage();
    }, 500);

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line
  }, [search]);

  const goToFirstPage = () => {
    tableRef.current && tableRef.current.resetPage(0);
  };

  const handleChangePage = (currentPage) => {
    if ((currentPage * rowsForPage) % 100 === 0) {
      getUsers(currentPage * rowsForPage);
    }
  };

  const getUsers = async (offset) => {
    setLoading(true);
    let data = {
      draw: "1",
      type: filter,
      columns: {},
      order: {
        0: {
          column: "0",
          dir: "asc",
        },
      },
      start: offset,
      length: 100,
      search: {
        value: search,
        regex: "false",
      },
    };
    const result = await dispatch(getUsersList(data));
    if (result.payload && result.payload.data) {
      setUsers(result.payload.data.data || result.payload.data);
      setTotalRows(result.payload.recordsTotal || result.payload.data.length);
      setCurrentOffset(offset);
    }
    setLoading(false);
  };

  const handleAddUser = () => {
    history.push("/admin/user");
  };

  const handleAddCustomer = () => {
    history.push("/admin/client");
  };

  const handleFilter = (type) => {
    setFilter(type);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    if (name === "search") setSearch(value);
  };

  const handleChangePassword = (id) => {
    setCurrentUserId(id);
    setMessageToast("");
    setPass1("");
    setPass2("");
    setModalChangePass(true);
    setMessage("");
  };

  const handleSavePassword = async () => {
    if (!pass1 || !pass2) {
      setMessage("Both password fields are required.");
      return;
    }
    if (pass1 !== pass2) {
      setMessage("Passwords do not match.");
      return;
    }
    const result = await dispatch(
      changePassword({ id: currentUserId, newpass: pass1 })
    );
    if (result.payload && result.payload.result) {
      setModalChangePass(false);
      setMessageToast("Password changed successfully.");
      setModalSuccess(true);
    } else {
      setMessageToast(
        result.payload.message ||
          "An error occurred while changing the password."
      );
    }
  };

  const fillButtons = (user) => (
    <GridContainer>
      <GridItem
        onClick={() => handleChangePassword(user.id)}
        style={{ cursor: "pointer" }}
        xs={12}
        sm={12}
        md={6}
      >
        <VpnKey style={{ color: "#13816F" }} fontSize="small"></VpnKey>
      </GridItem>
    </GridContainer>
  );

  const getActiveUsers = () => {
    let usersT = [];
    users.forEach((user, index) => {
      const rowIndex = currentOffset + index + 1;
      usersT[rowIndex] = [
        user.name,
        <div
          dangerouslySetInnerHTML={{ __html: sanatizeMessageHtml(user.role) }}
        />,
        user.agency,
        user.email,
        user.phone,
        user.status,
        user.last_login,
        fillButtons(user),
      ];
    });
    return (
      <GridContainer>
        <GridItem xs={12} style={{ marginLeft: "20px" }}>
          <GridContainer style={{ marginBottom: "20px" }}>
            <GridItem xs={12}>
              <Table
                ref={tableRef}
                tableHead={[
                  t("users.name") || "Name",
                  t("users.role") || "Role",
                  t("users.agency") || "Agency",
                  t("users.email") || "Email",
                  t("users.phone") || "Phone",
                  t("users.status") || "Status",
                  t("users.lastLogin") || "Last Login",
                  t("users.options") || "Options",
                ]}
                tableData={usersT}
                rowsPerPage={10}
                totalRows={totalRows}
                changePage={handleChangePage}
                loading={loading}
              />
            </GridItem>
          </GridContainer>
        </GridItem>
      </GridContainer>
    );
  };

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={12} md={12}>
          <GridContainer
            style={{ padding: "20px 0px 40px 0px" }}
            justify="center"
          >
            <GridContainer>
              <GridItem xs={12} md={12}>
                <GridItem xs={12} sm={12} md={12} lg={12}>
                  <Card>
                    <CardHeader>
                      <div className={classes.title + " " + classes.black}>
                        {t("users.title") || "Users"}
                      </div>
                      <GridContainer>
                        <GridItem xs={12} style={{ marginTop: "15px" }}>
                          <GridContainer>
                            <GridItem xs={0} sm={0} md={3}></GridItem>
                            <GridItem xs={12} sm={12} md={3}>
                              <Button
                                id="allUsers"
                                onClick={() => handleFilter("all")}
                                style={{ backgroundColor: "#008670" }}
                                block
                              >
                                {t("users.buttons.allUsers") || "All Users"}
                              </Button>
                            </GridItem>
                            <GridItem xs={12} sm={12} md={3}>
                              <Button
                                id="allAgents"
                                onClick={() => handleFilter("agent")}
                                style={{ backgroundColor: "#008670" }}
                                block
                              >
                                {t("users.buttons.allAgents") || "All Agents"}
                              </Button>
                            </GridItem>
                            <GridItem xs={12} sm={12} md={3}>
                              <Button
                                id="allCustomers"
                                onClick={() => handleFilter("customer")}
                                style={{ backgroundColor: "#008670" }}
                                block
                              >
                                {t("users.buttons.allCustomers") ||
                                  "All Customers"}
                              </Button>
                            </GridItem>
                          </GridContainer>
                        </GridItem>
                        <GridItem xs={12} style={{ marginTop: "15px" }}>
                          <GridContainer>
                            <GridItem xs={0} sm={0} md={6}></GridItem>
                            <GridItem xs={12} sm={12} md={3}>
                              <Button
                                id="addUser"
                                onClick={handleAddUser}
                                style={{ backgroundColor: "#008670" }}
                                block
                              >
                                <i className="fa fa-plus" />{" "}
                                {t("users.buttons.addUser") || "Add User"}
                              </Button>
                            </GridItem>
                            <GridItem xs={12} sm={12} md={3}>
                              <Button
                                id="addCustomer"
                                onClick={handleAddCustomer}
                                style={{ backgroundColor: "#008670" }}
                                block
                              >
                                <i className="fa fa-plus" />{" "}
                                {t("users.buttons.addCustomer") ||
                                  "Add Customer"}
                              </Button>
                            </GridItem>
                          </GridContainer>
                        </GridItem>
                        <GridItem xs={12} style={{ marginTop: "15px" }}>
                          <GridContainer>
                            <GridItem xs={0} sm={0} md={9}></GridItem>
                            <GridItem xs={12} sm={12} md={3}>
                              <CustomInput
                                labelText={t("users.search") || "Search"}
                                formControlProps={{
                                  fullWidth: true,
                                  className: classes.customFormControlClasses,
                                }}
                                inputProps={{
                                  name: "search",
                                  id: "search",
                                  value: search,
                                  onChange: handleFormChange,
                                }}
                              />
                            </GridItem>
                          </GridContainer>
                        </GridItem>
                        <GridItem xs={12} style={{ marginTop: "15px" }}>
                          {getActiveUsers()}
                        </GridItem>
                      </GridContainer>
                    </CardHeader>
                  </Card>
                </GridItem>
              </GridItem>
            </GridContainer>
          </GridContainer>
        </GridItem>
      </GridContainer>
      <CustomModal
        title={t("users.changePassword") || "Change Password"}
        isOpen={modalChangePass}
        onClose={() => setModalChangePass(false)}
        privacy={false}
        id={"changePassModal"}
      >
        <GridContainer>
          <GridItem xs={12}>
            <CustomInput
              labelText={t("users.password") || "Password"}
              inputProps={{
                type: "password",
                value: pass1,
                onChange: (e) => setPass1(e.target.value),
              }}
              formControlProps={{ fullWidth: true }}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomInput
              labelText={t("users.confirmPassword") || "Confirm Password"}
              inputProps={{
                type: "password",
                value: pass2,
                onChange: (e) => setPass2(e.target.value),
              }}
              formControlProps={{ fullWidth: true }}
            />
          </GridItem>
          {messageToast !== "" && (
            <GridItem xs={12}>
              <span style={{ color: "red" }}>{messageToast}</span>
            </GridItem>
          )}
          <GridItem xs={12}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={6}>
                <Button
                  variant="danger"
                  onClick={() => setModalChangePass(false)}
                  block
                >
                  {t("users.buttons.cancel") || "Cancel"}
                </Button>
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <Button variant="green" onClick={handleSavePassword} block>
                  {t("users.buttons.save") || "Save"}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </CustomModal>
      <CustomModal
        title="Success"
        isOpen={modalSuccess}
        onClose={() => {
          setModalSuccess(false);
          setMessageToast("");
        }}
        privacy={false}
        id={"successModal"}
      >
        <GridContainer>
          <GridItem xs={12}>
            <h4>{messageToast}</h4>
          </GridItem>
          <GridItem xs={12} sm={12} md={12}>
            <GridContainer>
              <GridItem xs={0} sm={0} md={2}></GridItem>
              <GridItem xs={12} sm={12} md={8}>
                <Button
                  id="successButton"
                  type="submit"
                  variant="green"
                  onClick={() => {
                    setModalSuccess(false);
                    setMessageToast("");
                  }}
                  block
                >
                  Ok
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </CustomModal>
      <Snackbar
        style={{ zIndex: "99999" }}
        place="center"
        color={typeToast}
        message={messageToast}
        open={toastMessage}
        closeNotification={() => setToastMessage(false)}
        close
      />
    </div>
  );
}
